import React from 'react';
import { ListChecks, ShieldCheck, Repeat } from 'lucide-react';
import { FracasIncident } from '../../types/fracas.types';

export const FracasCompliance: React.FC<{ incident: FracasIncident }> = ({ incident }) => {
    return (
        <>
            {/* IATF 16949 COMPLIANCE SECTION: CORRECTIVE ACTIONS */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ListChecks className="w-5 h-5 text-blue-600" />
                    Corrective Actions
                </h3>
                {incident.correctiveActions && incident.correctiveActions.length > 0 ? (
                    <div className="space-y-4">
                        {incident.correctiveActions.map((action, idx) => (
                            <div key={action.id || idx} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                <div className="flex justify-between items-start mb-2">
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${action.type === 'Permanent' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                                        {action.type}
                                    </span>
                                    <span className="text-sm text-gray-500">{new Date(action.dueDate).toLocaleDateString()}</span>
                                </div>
                                <p className="text-gray-800 font-medium mb-2">{action.description}</p>
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <span>Owner: {action.ownerName}</span>
                                    <span className="flex items-center gap-1">
                                        Status:
                                        <span className={`font-medium ${action.status === 'Verified' ? 'text-green-600' : 'text-gray-900'}`}>{action.status}</span>
                                    </span>
                                </div>
                                {action.effectiveness && (
                                    <div className="mt-2 text-sm border-t border-gray-200 pt-2">
                                        <span className="text-gray-500">Effectiveness: </span>
                                        <span className={`font-medium ${action.effectiveness === 'Effective' ? 'text-green-600' : 'text-orange-600'}`}>
                                            {action.effectiveness}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        <p className="text-gray-500">No corrective actions logged.</p>
                        <button className="mt-2 text-sm text-blue-600 font-medium hover:underline">
                            + Add Action
                        </button>
                    </div>
                )}
            </div>

            {/* IATF 16949 COMPLIANCE SECTION: VERIFICATION & LESSONS LEARNED */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-teal-600" />
                    Verification & Closure
                </h3>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Verification of Effectiveness</h4>
                        {incident.verificationResults ? (
                            <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg">
                                <p className="text-sm text-gray-800">{incident.verificationResults}</p>
                                <div className="mt-2 text-xs text-teal-700 flex justify-between">
                                    <span>Method: {incident.verificationMethod || 'Audit'}</span>
                                    {incident.verifiedBy && <span>Verified by: {incident.verifiedBy}</span>}
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 italic">Pending verification.</p>
                        )}
                    </div>

                    <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                            <Repeat className="w-3.5 h-3.5" /> Horizontal Deployment
                        </h4>
                        {incident.lessonsLearned ? (
                            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                <p className="text-sm text-gray-800">{incident.lessonsLearned}</p>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 italic">No lessons learned documented.</p>
                        )}
                    </div>
                </div>

                {incident.closedAt && (
                    <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-sm text-gray-600">Closed by <span className="font-medium">{incident.closedBy || 'Admin'}</span></span>
                        <span className="text-sm text-gray-600">{new Date(incident.closedAt).toLocaleString()}</span>
                    </div>
                )}
            </div>
        </>
    );
};
